import {
  useTheme,
  useMediaQuery,
  Typography,
  Box,
  Stack,
  IconButton,
  TextField,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Popover,
} from "@mui/material";
import {
  AdjustmentsHorizontalIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  TagIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { DatePicker } from "@mui/x-date-pickers-pro";
import i18next from "i18next";
import { FC } from "react";
import dayjs from "dayjs";

import { statusFilterOptions } from "../../../utils/filter";
import { FilterValues } from "../../../@types/filter";
import { Category } from "../../../enums/Category";

interface AdvancedFiltersPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  filterValues: FilterValues;
  onFilterUpdate: (filterValues: FilterValues) => void;
  includeAdvances?: boolean;
  isRefundRequestPage: boolean;
  selfRefundsOnly: boolean;
  onApplyFilters?: () => void;
}

interface FilterContentProps {
  showHeader?: boolean;
  isMobile: boolean;
  padding: number;
  filterValues: FilterValues;
  onFilterUpdate: (filterValues: FilterValues) => void;
  selfRefundsOnly: boolean;
  isRefundRequestPage: boolean;
  includeAdvances: boolean;
}


const FilterContent: FC<FilterContentProps> = ({
  showHeader = false,
  isMobile,
  padding,
  filterValues,
  onFilterUpdate,
  selfRefundsOnly,
  isRefundRequestPage,
  includeAdvances = false,
}) => (
  <>
    {showHeader && (
      <Typography
        variant={isMobile ? "h6" : "h6"}
        sx={{
          mb: padding,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: isMobile ? "1.1rem" : "1.25rem",
        }}
      >
        <AdjustmentsHorizontalIcon className={isMobile ? "w-5 h-5" : "w-4 h-4"} />
        Filtros
      </Typography>
    )}

      {/* Colaborador */}
      {!selfRefundsOnly && !isRefundRequestPage && (
        <>
          <Box sx={{ mb: padding }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1.5,
                color: "text.secondary",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: isMobile ? "0.875rem" : "0.8125rem",
              }}
            >
              <UserGroupIcon className="w-4 h-4" />
              Colaborador
            </Typography>

            <TextField
              label="Nome"
              fullWidth
              variant="outlined"
              value={filterValues.collaboratorName || ""}
              // key={`collaborator-${filterValues.collaboratorName || "empty"}`}
              onChange={(e) => {
                  const newFilterValues = { ...filterValues };
                  newFilterValues.collaboratorName = e.target.value;
                  if (e.target.value === "") {
                    newFilterValues.collaborator = null;
                  }
                  onFilterUpdate(newFilterValues);
                }}
              size="small"
              placeholder="Digite o nome do colaborador..."
            />
          </Box>
          <Divider sx={{ mb: padding }} />
        </>
      )}

      {/* Gestor */}
      {!selfRefundsOnly && !isRefundRequestPage && (
        <>
          <Box sx={{ mb: padding }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1.5,
                color: "text.secondary",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: isMobile ? "0.875rem" : "0.8125rem",
              }}
            >
              <UserGroupIcon className="w-4 h-4" />
              Gestor
            </Typography>

            <TextField
              label="Nome"
              fullWidth
              variant="outlined"
              value={filterValues.ownerName || ""}
              // key={`owner-${filterValues.ownerName || "empty"}`}
              onChange={(e) => {
                  const newFilterValues = { ...filterValues };
                  newFilterValues.ownerName = e.target.value;
                  onFilterUpdate(newFilterValues);
                }}
              size="small"
              placeholder="Digite o nome do gestor..."
            />
          </Box>
          <Divider sx={{ mb: padding }} />
        </>
      )}

      {/* Status */}
      <Box sx={{ mb: padding }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1.5,
            color: "text.secondary",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: isMobile ? "0.875rem" : "0.8125rem",
          }}
        >
          <TagIcon className="w-4 h-4" />
          Status
        </Typography>

      <FormControl fullWidth size="small">
        <InputLabel>Status</InputLabel>
        <Select
          value={filterValues.statusFilter?.key || ""}
          label="Status"
          onChange={(e) => {
            const newFilterValues = { ...filterValues };
            if (e.target.value === "") {
              newFilterValues.statusFilter = null;
            } else {
              newFilterValues.statusFilter = statusFilterOptions[e.target.value as keyof typeof statusFilterOptions];
            }
            onFilterUpdate(newFilterValues);
          }}
          endAdornment={
            filterValues.statusFilter && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const newFilterValues = { ...filterValues };
                  newFilterValues.statusFilter = null;
                  onFilterUpdate(newFilterValues);
                }}
                sx={{
                  position: "absolute",
                  right: 32,
                  color: "text.secondary",
                  "&:hover": {
                    color: "error.main",
                  },
                }}
              >
                <XCircleIcon className="w-4 h-4" />
              </IconButton>
            )
          }
        >
          <MenuItem value="">- Selecione -</MenuItem>
          {Object.keys(statusFilterOptions).map((key) => (
            <MenuItem key={key} value={key}>
              {statusFilterOptions[key]?.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>

    <Divider sx={{ mb: padding }} />

      {/* Organization */}
      <Box sx={{ mb: padding }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1.5,
            color: "text.secondary",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: isMobile ? "0.875rem" : "0.8125rem",
          }}
        >
          <CurrencyDollarIcon className="w-4 h-4" />
          Organização
        </Typography>

        <Stack spacing={2}>
          {/* Cost Center */}
          <TextField
            label="Centro de Custo"
            fullWidth
            variant="outlined"
            value={filterValues.costCenter || ""}
            onChange={(e) => {
                const newFilterValues = { ...filterValues };
                newFilterValues.costCenter = e.target.value;
                onFilterUpdate(newFilterValues);
              }}
            size="small"
            placeholder="Digite o centro de custo..."
          />

        {/* Type */}
        {includeAdvances && (
          <FormControl fullWidth size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              value={filterValues.isAdvance === undefined ? "" : filterValues.isAdvance ? "advance" : "refund"}
              label="Tipo"
              onChange={(e) => {
                const newFilterValues = { ...filterValues };
                if (e.target.value === "") {
                  newFilterValues.isAdvance = undefined;
                } else if (e.target.value === "advance") {
                  newFilterValues.isAdvance = true;
                } else if (e.target.value === "refund") {
                  newFilterValues.isAdvance = false;
                }
                onFilterUpdate(newFilterValues);
              }}
              endAdornment={
                filterValues.isAdvance !== undefined && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newFilterValues = { ...filterValues };
                      newFilterValues.isAdvance = undefined;
                      onFilterUpdate(newFilterValues);
                    }}
                    sx={{
                      position: "absolute",
                      right: 32,
                      color: "text.secondary",
                      "&:hover": {
                        color: "error.main",
                      },
                    }}
                  >
                    <XCircleIcon className="w-4 h-4" />
                  </IconButton>
                )
              }
            >
              <MenuItem value="">- Todos -</MenuItem>
              <MenuItem value="refund">Reembolso</MenuItem>
              <MenuItem value="advance">Adiantamento</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Client Refund */}
        <FormControl fullWidth size="small">
          <InputLabel>Reembolso do Cliente</InputLabel>
          <Select
            value={filterValues.clientRefund === undefined || filterValues.clientRefund === null ? "" : filterValues.clientRefund ? "true" : "false"}
            label="Reembolso do Cliente"
            onChange={(e) => {
              const newFilterValues = { ...filterValues };
              if (e.target.value === "") {
                newFilterValues.clientRefund = null;
              } else if (e.target.value === "true") {
                newFilterValues.clientRefund = true;
              } else if (e.target.value === "false") {
                newFilterValues.clientRefund = false;
              }
              onFilterUpdate(newFilterValues);
            }}
            endAdornment={
              filterValues.clientRefund !== undefined && filterValues.clientRefund !== null && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newFilterValues = { ...filterValues };
                    newFilterValues.clientRefund = null;
                    onFilterUpdate(newFilterValues);
                  }}
                  sx={{
                    position: "absolute",
                    right: 32,
                    color: "text.secondary",
                    "&:hover": {
                      color: "error.main",
                    },
                  }}
                >
                  <XCircleIcon className="w-4 h-4" />
                </IconButton>
              )
            }
          >
            <MenuItem value="">- Todos -</MenuItem>
            <MenuItem value="true">Sim</MenuItem>
            <MenuItem value="false">Não</MenuItem>
          </Select>
        </FormControl>

        {/* Expense Category */}
        <FormControl fullWidth size="small">
          <InputLabel>Categoria de Despesa</InputLabel>
          <Select
            value={filterValues.expenseCategory || ""}
            label="Categoria de Despesa"
            onChange={(e) => {
              const newFilterValues = { ...filterValues };
              newFilterValues.expenseCategory = e.target.value || null;
              onFilterUpdate(newFilterValues);
            }}
            endAdornment={
              filterValues.expenseCategory && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newFilterValues = { ...filterValues };
                    newFilterValues.expenseCategory = null;
                    onFilterUpdate(newFilterValues);
                  }}
                  sx={{
                    position: "absolute",
                    right: 32,
                    color: "text.secondary",
                    "&:hover": {
                      color: "error.main",
                    },
                  }}
                >
                  <XCircleIcon className="w-4 h-4" />
                </IconButton>
              )
            }
          >
            <MenuItem value="">- Todas as Categorias -</MenuItem>
            {Object.values(Category).map((categoryValue) => (
              <MenuItem key={categoryValue} value={categoryValue}>
                {i18next.t(`category.${categoryValue}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Box>

    <Divider sx={{ mb: padding }} />

    {/* Dates and Periods */}
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.5,
          color: "text.secondary",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: isMobile ? "0.875rem" : "0.8125rem",
        }}
      >
        <CalendarIcon className="w-4 h-4" />
        Datas e Períodos
      </Typography>

      <Stack spacing={2}>
        {/* Creation Date */}
        <Box>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary", fontWeight: 500 }}>
            Data de criação
          </Typography>
          <Stack direction={isMobile ? "column" : "row"} spacing={1}>
            <DatePicker
              label="Data Início"
              value={filterValues.createDateAfter ? dayjs(filterValues.createDateAfter) : null}
              slotProps={{
                textField: { size: "small", fullWidth: true },
                actionBar: { actions: ["clear"] },
              }}
              format="DD/MM/YY"
              onChange={(date) => {
                const newFilterValues = { ...filterValues };
                newFilterValues.createDateAfter = date?.format("YYYY-MM-DDT00:00:00") ?? null;
                onFilterUpdate(newFilterValues);
              }}
            />
            <DatePicker
              label="Data Fim"
              value={filterValues.createDateUntil ? dayjs(filterValues.createDateUntil) : null}
              slotProps={{
                textField: { size: "small", fullWidth: true },
                actionBar: { actions: ["clear"] },
              }}
              format="DD/MM/YY"
              onChange={(date) => {
                const newFilterValues = { ...filterValues };
                newFilterValues.createDateUntil = date?.format("YYYY-MM-DDT23:59:59") ?? null;
                onFilterUpdate(newFilterValues);
              }}
            />
          </Stack>
        </Box>

        {/* Período Geral */}
        <Box>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary", fontWeight: 500 }}>
            Período das despesas
          </Typography>
          <Stack direction={isMobile ? "column" : "row"} spacing={1}>
            <DatePicker
              label="Data Início"
              value={filterValues.startDate ? dayjs(filterValues.startDate) : null}
              slotProps={{
                textField: { size: "small", fullWidth: true },
                actionBar: { actions: ["clear"] },
              }}
              format="DD/MM/YY"
              onChange={(date) => {
                const newFilterValues = { ...filterValues };
                newFilterValues.startDate = date?.format("YYYY-MM-DD") ?? null;
                onFilterUpdate(newFilterValues);
              }}
            />
            <DatePicker
              label="Data Fim"
              value={filterValues.endDate ? dayjs(filterValues.endDate) : null}
              slotProps={{
                textField: { size: "small", fullWidth: true },
                actionBar: { actions: ["clear"] },
              }}
              format="DD/MM/YY"
              onChange={(date) => {
                const newFilterValues = { ...filterValues };
                newFilterValues.endDate = date?.format("YYYY-MM-DD") ?? null;
                onFilterUpdate(newFilterValues);
              }}
            />
          </Stack>
        </Box>

        {/* Approvals Gestor */}
        <Box>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary", fontWeight: 500 }}>
            Aprovação do gestor
          </Typography>
          <Stack spacing={1}>
            <DatePicker
              label="Aprovado pelo gestor até"
              value={filterValues.ownerApprovedDateUntil ? dayjs(filterValues.ownerApprovedDateUntil) : null}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "Aprovado pelo gestor até...",
                },
                actionBar: { actions: ["clear"] },
              }}
              format="DD/MM/YY"
              onChange={(date) => {
                const newFilterValues = { ...filterValues };
                newFilterValues.ownerApprovedDateUntil = date?.format("YYYY-MM-DDT23:59:59") ?? null;
                onFilterUpdate(newFilterValues);
              }}
            />

            <DatePicker
              label="Aprovado pelo gestor após"
              value={filterValues.ownerApprovedDateAfter ? dayjs(filterValues.ownerApprovedDateAfter) : null}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "Aprovado pelo gestor após...",
                },
                actionBar: { actions: ["clear"] },
              }}
              format="DD/MM/YY"
              onChange={(date) => {
                const newFilterValues = { ...filterValues };
                newFilterValues.ownerApprovedDateAfter = date?.format("YYYY-MM-DDT23:59:59") ?? null;
                onFilterUpdate(newFilterValues);
              }}
            />
          </Stack>
        </Box>

        {/* Approvals Financial */}
        <Box>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary", fontWeight: 500 }}>
            Aprovação do financeiro
          </Typography>
          <Stack spacing={1}>
            <DatePicker
              label="Aprovado pelo financeiro até"
              value={filterValues.financialApproveDateUntil ? dayjs(filterValues.financialApproveDateUntil) : null}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "Aprovado pelo financeiro até...",
                },
                actionBar: { actions: ["clear"] },
              }}
              format="DD/MM/YY"
              onChange={(date) => {
                const newFilterValues = { ...filterValues };
                newFilterValues.financialApproveDateUntil = date?.format("YYYY-MM-DD") ?? null;
                onFilterUpdate(newFilterValues);
              }}
            />

            <DatePicker
              label="Aprovado pelo financeiro após"
              value={filterValues.financialApproveDateAfter ? dayjs(filterValues.financialApproveDateAfter) : null}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "Aprovado pelo financeiro após...",
                },
                actionBar: { actions: ["clear"] },
              }}
              format="DD/MM/YY"
              onChange={(date) => {
                const newFilterValues = { ...filterValues };
                newFilterValues.financialApproveDateAfter = date?.format("YYYY-MM-DD") ?? null;
                onFilterUpdate(newFilterValues);
              }}
            />
          </Stack>
        </Box>
      </Stack>
    </Box>
  </>
);

const AdvancedFiltersPopover: FC<AdvancedFiltersPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  filterValues,
  onFilterUpdate,
  includeAdvances = false,
  isRefundRequestPage,
  selfRefundsOnly,
  onApplyFilters,
}) => {
  const theme = useTheme();

  // Breakpoints responsivos
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isShortScreen = useMediaQuery("(max-height: 700px)");
  const isVeryShortScreen = useMediaQuery("(max-height: 600px)");

 
  // Configurações responsivas
  const getResponsiveConfig = () => {
    if (isMobile) {
      return {
        component: "dialog" as const,
        width: "95vw",
        maxWidth: 400,
        maxHeight: isVeryShortScreen ? "85vh" : "90vh",
        padding: 2,
      };
    }

    if (isTablet || isShortScreen) {
      return {
        component: "popover" as const,
        width: 320,
        maxWidth: "90vw",
        maxHeight: isVeryShortScreen ? "75vh" : isShortScreen ? "80vh" : "85vh",
        padding: 2.5,
      };
    }

    return {
      component: "popover" as const,
      width: 360,
      maxWidth: 400,
      maxHeight: "85vh",
      padding: 3,
    };
  };

  const config = getResponsiveConfig();
  //funções auxiliares
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if(onApplyFilters) {  
      onClose();
      onApplyFilters();
    }

  };

  const filterContentProps: FilterContentProps = {
    isMobile,
    padding: config.padding,
    filterValues,
    onFilterUpdate,
    selfRefundsOnly,
    isRefundRequestPage,
    includeAdvances,
  };

  // conditional rendering, dialog for mobile, popover for desktop/tablet
  if (config.component === "dialog") {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: config.width,
            maxWidth: config.maxWidth,
            maxHeight: config.maxHeight,
            borderRadius: 2,
            m: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AdjustmentsHorizontalIcon className="w-5 h-5" />
            Filtros Avançados
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {onApplyFilters && (
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  onClose();
                  onApplyFilters();
                }}
                sx={{ textTransform: "none" }}
              >
                Aplicar
              </Button>
            )}
            <IconButton size="small" onClick={onClose}>
              <XCircleIcon className="w-5 h-5" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 1 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <FilterContent {...filterContentProps} />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Popover for larger screens
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            width: config.width,
            maxWidth: config.maxWidth,
            maxHeight: config.maxHeight,
            borderRadius: 2,
            boxShadow: 3,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {/* Cabeçalho fixo com título e botão Aplicar */}
      <Box
        sx={{
          px: config.padding,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: isMobile ? "1.1rem" : "1.25rem",
          }}
        >
          <AdjustmentsHorizontalIcon className="w-4 h-4" />
          Filtros
        </Typography>
        {onApplyFilters && (
          <Button
            type="submit"
            form="advanced-filters-form"
            size="small"
            variant="contained"
            sx={{ textTransform: "none", fontSize: "0.875rem" }}
          >
            Aplicar Filtros
          </Button>
        )}
      </Box>

      {/* Conteúdo rolável */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        id="advanced-filters-form"
        sx={{
          p: config.padding,
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.divider,
            borderRadius: "3px",
            "&:hover": {
              background: theme.palette.text.secondary,
            },
          },
        }}
      >
        <FilterContent {...filterContentProps} />
      </Box>
    </Popover>
  );
};

export default AdvancedFiltersPopover;
